import axios from 'axios';
import { setSecureItem, getSecureItem, removeSecureItem } from '../utils/localStorage';
import { isTokenExpired, isTokenExpiringSoon } from '../utils/jwt';

// Create a separate axios instance for auth calls to avoid circular interceptor issues
const authAxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

interface LoginCredentials {
  email: string;
  password: string;
}

interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

interface UserData {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: UserData;
}

export const authService = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    try {
      const response = await authAxiosInstance.post<LoginResponse>('/auth/login', credentials);
      // Handle the response structure
      const responseData = response.data;
      
      // Check if we have the expected data
      if (responseData.accessToken && responseData.user) {
        setSecureItem('accessToken', responseData.accessToken);
        setSecureItem('refreshToken', responseData.refreshToken);

        return responseData;
      } else {
        console.error('Invalid response structure:', responseData);
        throw new Error('Invalid response structure from server');
      }
    } catch (error: any) {
      console.error('Auth service login error:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  },

  refreshToken: async (): Promise<RefreshTokenResponse> => {
    
    const refreshToken = getSecureItem('refreshToken');
    if (!refreshToken) {
      console.error('❌ No refresh token available');
      throw new Error('No refresh token available');
    }

    // Check if refresh token is expired
    if (isTokenExpired(refreshToken)) {
      console.warn('❌ Refresh token is expired, cannot refresh access token');
      throw new Error('Refresh token expired');
    }

    try {
      // Use the separate auth axios instance to avoid circular interceptor calls
      const response = await authAxiosInstance.post<RefreshTokenResponse>('/auth/refresh', {
        refreshToken
      });
      
      if (response.data?.accessToken) {
        setSecureItem('accessToken', response.data.accessToken);
        if (response.data?.refreshToken) {
          setSecureItem('refreshToken', response.data.refreshToken);
        }
      }
      
      return response.data;
    } catch (error: any) {
      console.error('❌ Token refresh failed:', error);
      
      // If refresh fails, clear tokens and throw error
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('❌ Refresh token rejected by server, clearing tokens');
        removeSecureItem('accessToken');
        removeSecureItem('refreshToken');
      }
      
      throw error;
    }
  },

  // Check if access token needs refresh (expiring within 5 minutes)
  shouldRefreshToken: (): boolean => {
    const accessToken = getSecureItem('accessToken');
    if (!accessToken) return false;
    
    return isTokenExpiringSoon(accessToken);
  },

  // Check if access token is expired
  isAccessTokenExpired: (): boolean => {
    const accessToken = getSecureItem('accessToken');
    if (!accessToken) return true;
    
    return isTokenExpired(accessToken);
  },

  // Check if refresh token is expired
  isRefreshTokenExpired: (): boolean => {
    const refreshToken = getSecureItem('refreshToken');
    if (!refreshToken) return true;
    
    return isTokenExpired(refreshToken);
  },

  // Get current token status
  getTokenStatus: () => {
    const accessToken = getSecureItem('accessToken');
    const refreshToken = getSecureItem('refreshToken');
    
    if (!accessToken || !refreshToken) {
      return {
        hasAccessToken: false,
        hasRefreshToken: false,
        accessTokenExpired: true,
        refreshTokenExpired: true,
        needsRefresh: false,
        canRefresh: false
      };
    }
    
    const accessTokenExpired = isTokenExpired(accessToken);
    const refreshTokenExpired = isTokenExpired(refreshToken);
    const needsRefresh = isTokenExpiringSoon(accessToken);
    
    const status = {
      hasAccessToken: true,
      hasRefreshToken: true,
      accessTokenExpired,
      refreshTokenExpired,
      needsRefresh,
      canRefresh: !refreshTokenExpired
    };

    return status;
  },
  refreshTokenIfNeeded: async function(): Promise<boolean> {
    try {
      if (this.shouldRefreshToken() && !this.isRefreshTokenExpired()) {
        await this.refreshToken();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Proactive token refresh failed:', error);
      return false;
    }
  },


  logout: () => {
    removeSecureItem('accessToken');
    removeSecureItem('refreshToken');
  },

  getCurrentUser: () => {
    const token = getSecureItem('accessToken');
    return token ? true : false;
  },

  getUsers: async () => {
    try {
      // Import axiosInstance here to avoid circular dependency
      const { default: axiosInstance } = await import('../lib/axios');
      const response = await axiosInstance.get('/employees');
      return response;
    } catch (error) {
      console.error('Failed to fetch users:', error);
      throw error;
    }
  }
}; 